/**
 * SectionBackgroundControls — reusable "section background" panel for the
 * module instance editors. Writes `bg_color`, `bg_image`, `bg_size`,
 * `bg_position`, `bg_repeat` into the instance config, all of which are
 * picked up by the ModularPage wrapper.
 *
 * All fields are optional. Setting a color/image extends the wrapper
 * edge-to-edge on the public page even for contained modules, so the
 * section still feels distinct now that modules sit flush.
 */
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { getApiUrl } from "@/utils/api";
import { Loader2, Upload, X, Bookmark, Trash2 } from "lucide-react";
import ColorPickerInput from "@/components/ui/ColorPickerInput";

const API = getApiUrl();

export default function SectionBackgroundControls({ config = {}, onChange }) {
  const fileRef = useRef(null);
  const uploadingRef = useRef(false);
  // Named background presets scoped to the tenant. Loaded once per mount
  // and refreshed on every save/delete so the picker stays in sync
  // across dialogs opened in the same session.
  const [presets, setPresets] = useState([]);
  const [savingPreset, setSavingPreset] = useState(false);
  const [showSaveInput, setShowSaveInput] = useState(false);
  const [presetName, setPresetName] = useState("");

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/admin/bg-presets`, { withCredentials: true })
      .then(({ data }) => { if (alive) setPresets(data.presets || []); })
      .catch(() => { /* silent — presets are optional */ });
    return () => { alive = false; };
  }, []);

  const refreshPresets = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/bg-presets`, { withCredentials: true });
      setPresets(data.presets || []);
    } catch { /* noop */ }
  };

  const applyPreset = (preset) => {
    if (!preset) return;
    onChange?.({
      bg_color: preset.config?.bg_color || "",
      bg_image: preset.config?.bg_image || "",
      bg_size: preset.config?.bg_size || "cover",
      bg_position: preset.config?.bg_position || "center",
      bg_repeat: preset.config?.bg_repeat || "no-repeat",
    });
  };

  const savePreset = async () => {
    const name = presetName.trim();
    if (!name) {
      toast.error("Give the preset a name first");
      return;
    }
    if (!config.bg_color && !config.bg_image) {
      toast.error("Set a colour or image before saving");
      return;
    }
    setSavingPreset(true);
    try {
      await axios.post(`${API}/admin/bg-presets`, {
        name,
        config: {
          bg_color: config.bg_color || "",
          bg_image: config.bg_image || "",
          bg_size: config.bg_size || "cover",
          bg_position: config.bg_position || "center",
          bg_repeat: config.bg_repeat || "no-repeat",
        },
      }, { withCredentials: true });
      toast.success(`Saved preset "${name}"`);
      setShowSaveInput(false);
      setPresetName("");
      await refreshPresets();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save preset");
    } finally {
      setSavingPreset(false);
    }
  };

  const deletePreset = async (preset) => {
    if (!window.confirm(`Delete preset "${preset.name}"?`)) return;
    try {
      await axios.delete(`${API}/admin/bg-presets/${preset.preset_id}`, { withCredentials: true });
      toast.success("Preset deleted");
      await refreshPresets();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete");
    }
  };

  const handleUpload = async (file) => {
    if (!file || uploadingRef.current) return;
    uploadingRef.current = true;
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/admin/upload`, fd, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = data.url || data.path;
      if (url) onChange?.({ bg_image: url });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      uploadingRef.current = false;
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const clearImage = () => onChange?.({ bg_image: "" });
  const clearColor = () => onChange?.({ bg_color: "" });

  return (
    <div className="pt-4 border-t" data-testid="section-background-controls">
      <div className="text-xs font-medium mb-2 text-muted-foreground uppercase tracking-wide">
        Section background
      </div>

      {/* Presets picker + save-as — lets admins reuse a named background
          config across module instances with one click. */}
      <div className="mb-3">
        <div className="flex items-center gap-2">
          <select
            value=""
            onChange={(e) => {
              const p = presets.find((x) => x.preset_id === e.target.value);
              if (p) applyPreset(p);
              e.target.value = "";
            }}
            className="flex-1 h-9 px-2 border border-input rounded text-sm"
            data-testid="section-bg-preset-picker"
          >
            <option value="">{presets.length ? "Apply a saved preset…" : "No saved presets yet"}</option>
            {presets.map((p) => (
              <option key={p.preset_id} value={p.preset_id}>{p.name}</option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => setShowSaveInput((v) => !v)}
            className="h-9 px-3 border border-input rounded text-xs inline-flex items-center gap-1 hover:bg-muted/40"
            title="Save current colour/image as a preset"
            data-testid="section-bg-preset-save-toggle"
          >
            <Bookmark className="h-3.5 w-3.5" /> Save as
          </button>
        </div>
        {showSaveInput && (
          <div className="mt-2 flex items-center gap-2">
            <input
              type="text"
              placeholder="Preset name (e.g. Cream cards)"
              value={presetName}
              onChange={(e) => setPresetName(e.target.value)}
              className="flex-1 h-9 px-3 border border-input rounded text-sm"
              data-testid="section-bg-preset-name"
              autoFocus
            />
            <button
              type="button"
              onClick={savePreset}
              disabled={savingPreset || !presetName.trim()}
              className="h-9 px-3 border border-input rounded text-xs bg-primary text-white hover:bg-primary/90 disabled:opacity-50"
              data-testid="section-bg-preset-save"
            >
              {savingPreset ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Save"}
            </button>
          </div>
        )}
        {presets.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {presets.map((p) => {
              const swatchImage = p.config?.bg_image || "";
              const swatchColor = p.config?.bg_color || "";
              return (
                <div key={p.preset_id} className="inline-flex items-center gap-1.5 px-2 py-1 bg-muted/40 border border-input rounded text-xs">
                  <button
                    type="button"
                    onClick={() => applyPreset(p)}
                    className="inline-flex items-center gap-1.5 hover:underline"
                    title={swatchImage ? "Image background" : swatchColor ? "Colour background" : ""}
                    data-testid={`section-bg-preset-apply-${p.preset_id}`}
                  >
                    <span
                      className="h-4 w-4 rounded-sm border border-input/60 shrink-0"
                      style={
                        swatchImage
                          ? { backgroundImage: `url("${swatchImage}")`, backgroundSize: "cover", backgroundPosition: "center" }
                          : { backgroundColor: swatchColor || "transparent" }
                      }
                      data-testid={`section-bg-preset-swatch-${p.preset_id}`}
                    />
                    {p.name}
                  </button>
                  <button
                    type="button"
                    onClick={() => deletePreset(p)}
                    className="text-muted-foreground hover:text-red-600"
                    title={`Delete "${p.name}"`}
                    data-testid={`section-bg-preset-delete-${p.preset_id}`}
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="grid grid-cols-[auto_1fr] gap-3 items-start">
        <div>
          <label className="text-xs font-medium mb-1 block">Colour</label>
          <div className="flex items-center gap-2">
            <ColorPickerInput
              value={config.bg_color || ""}
              onChange={(v) => onChange?.({ bg_color: v })}
              data-testid="section-bg-color"
            />
            {config.bg_color && (
              <button
                type="button"
                onClick={clearColor}
                className="text-xs text-muted-foreground hover:text-foreground"
                title="Clear background colour"
                data-testid="section-bg-color-clear"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>

        <div>
          <label className="text-xs font-medium mb-1 block">Image</label>
          {config.bg_image ? (
            <div className="flex items-center gap-2">
              <div
                className="h-9 w-16 border border-input rounded bg-cover bg-center"
                style={{ backgroundImage: `url("${config.bg_image}")` }}
                data-testid="section-bg-image-preview"
              />
              <button
                type="button"
                onClick={clearImage}
                className="text-xs text-red-600 hover:underline"
                data-testid="section-bg-image-clear"
              >
                Remove
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="h-9 px-3 border border-dashed border-input rounded text-xs inline-flex items-center gap-2 hover:bg-muted/40"
              data-testid="section-bg-image-upload"
            >
              {uploadingRef.current ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Upload className="h-3.5 w-3.5" />
              )}
              Upload image
            </button>
          )}
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => handleUpload(e.target.files?.[0])}
          />
        </div>
      </div>

      {config.bg_image && (
        <div className="grid grid-cols-3 gap-3 mt-3">
          <div>
            <label className="text-xs font-medium mb-1 block">Size</label>
            <select
              value={config.bg_size || "cover"}
              onChange={(e) => onChange?.({ bg_size: e.target.value })}
              className="w-full h-9 px-2 border border-input rounded text-sm"
              data-testid="section-bg-size"
            >
              <option value="cover">Cover</option>
              <option value="contain">Contain</option>
              <option value="auto">Auto</option>
              <option value="100% 100%">Stretch</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-medium mb-1 block">Position</label>
            <select
              value={config.bg_position || "center"}
              onChange={(e) => onChange?.({ bg_position: e.target.value })}
              className="w-full h-9 px-2 border border-input rounded text-sm"
              data-testid="section-bg-position"
            >
              <option value="center">Center</option>
              <option value="top">Top</option>
              <option value="bottom">Bottom</option>
              <option value="left">Left</option>
              <option value="right">Right</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-medium mb-1 block">Repeat</label>
            <select
              value={config.bg_repeat || "no-repeat"}
              onChange={(e) => onChange?.({ bg_repeat: e.target.value })}
              className="w-full h-9 px-2 border border-input rounded text-sm"
              data-testid="section-bg-repeat"
            >
              <option value="no-repeat">No repeat</option>
              <option value="repeat">Repeat</option>
              <option value="repeat-x">Repeat X</option>
              <option value="repeat-y">Repeat Y</option>
            </select>
          </div>
        </div>
      )}

      <p className="text-xs text-muted-foreground mt-2">
        Extends edge-to-edge behind this module on the public page. Colour + image compose (image sits on top of the colour).
      </p>
    </div>
  );
}
